import { useEffect, useMemo, useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import { Button } from '@/components/ui/button';
import {
  ArrowUpRight,
  ShieldAlert,
  Brain,
  BarChart3,
  Megaphone,
  Rss,
  Newspaper,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CATEGORIES = [
  { id: 'all',           label: 'All',           icon: Newspaper },
  { id: 'cybersecurity', label: 'Cybersecurity', icon: ShieldAlert },
  { id: 'ai',            label: 'AI',            icon: Brain },
  { id: 'data',          label: 'Data',          icon: BarChart3 },
  { id: 'growth',        label: 'Growth',        icon: Megaphone },
];

function formatDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now - d;
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHrs < 1) return 'Just now';
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function hostnameOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

export default function BlogPage() {
  useSEO({
    title: 'Blog | Acodia — Cybersecurity, AI, Data and Growth News',
    description: 'Curated news and insight from the worlds of cybersecurity, artificial intelligence, data analytics, and digital growth — updated continuously.',
    path: '/blog',
    ogImage: `${SITE_URL}/images/og/og-default.png`,
  });

  const [category, setCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const url = `${BACKEND_URL}/api/news?category=${encodeURIComponent(category)}&limit=30`;
    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Feed service returned ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setArticles(Array.isArray(data.articles) ? data.articles : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load news.');
        setArticles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [category, refreshKey]);

  const featured = articles[0];
  const rest = useMemo(() => articles.slice(1), [articles]);

  return (
    <div
      data-testid="blog-page"
      style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--white)' }}
    >
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-14 sm:pt-40 sm:pb-16 border-b"
        style={{ background: 'var(--bg)', borderColor: 'var(--b1)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: [
              'radial-gradient(ellipse 70% 50% at 60% 30%, rgba(139,92,246,0.08) 0%, transparent 60%)',
              'radial-gradient(ellipse 50% 60% at 20% 70%, rgba(192,132,252,0.05) 0%, transparent 60%)',
            ].join(', '),
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex items-center gap-2 mb-3" style={{ color: 'var(--a)' }}>
              <Rss size={14} />
              <p className="font-body text-xs uppercase tracking-[0.22em]">Acodia Signal</p>
            </div>
            <h1
              className="font-display font-extrabold tracking-[-0.03em] mb-5 max-w-[22ch]"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', color: 'var(--white)' }}
            >
              The News We Read{' '}
              <span style={{ color: 'var(--a)' }}>So You Do Not Have To.</span>
            </h1>
            <p
              className="font-body font-light text-base sm:text-lg max-w-3xl"
              style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.72' }}
            >
              A curated live feed from the best cybersecurity, AI, data, and digital growth publications
              in the world. Updated continuously. No fluff, no affiliate links, no ads — just the stories
              that matter to anyone building or defending a modern business.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Category filter bar */}
      <section
        className="sticky top-16 z-30 border-b backdrop-blur"
        style={{ background: 'rgba(5,5,10,0.85)', borderColor: 'var(--b1)' }}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 py-3 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-0">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`blog-cat-${c.id}`}
                    onClick={() => setCategory(c.id)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-[3px] font-body text-sm whitespace-nowrap transition-all"
                    style={{
                      color: active ? 'var(--white)' : 'var(--ts)',
                      background: active ? 'rgba(139,92,246,0.12)' : 'transparent',
                      border: active ? '1px solid var(--b3)' : '1px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.color = 'var(--white)';
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.color = 'var(--ts)';
                    }}
                  >
                    <Icon size={14} />
                    {c.label}
                  </button>
                );
              })}
            </div>

            <button
              data-testid="blog-refresh"
              onClick={() => setRefreshKey((k) => k + 1)}
              aria-label="Refresh feed"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-[3px] font-body text-sm"
              style={{ color: 'var(--ts)', background: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--white)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ts)'; }}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </section>

      {/* Feed body */}
      <section className="py-10 sm:py-14" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          {error && !loading && (
            <div
              data-testid="blog-error"
              className="p-5 rounded-lg mb-8 flex items-start gap-3"
              style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.25)' }}
            >
              <AlertCircle size={18} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <p className="font-body font-medium" style={{ color: 'var(--white)' }}>
                  We could not reach the news service.
                </p>
                <p className="font-body text-sm mt-1" style={{ color: 'rgba(240,237,248,0.78)' }}>
                  {error}. Try again in a moment.
                </p>
              </div>
            </div>
          )}

          {loading && (
            <div data-testid="blog-loading" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="p-6 rounded-lg"
                  style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
                >
                  <div style={{ height: 12, width: '40%', background: 'var(--b1)', borderRadius: 4, marginBottom: 12 }} />
                  <div style={{ height: 18, width: '90%', background: 'var(--b1)', borderRadius: 4, marginBottom: 8 }} />
                  <div style={{ height: 18, width: '70%', background: 'var(--b1)', borderRadius: 4, marginBottom: 16 }} />
                  <div style={{ height: 10, width: '100%', background: 'var(--b1)', borderRadius: 4, marginBottom: 6 }} />
                  <div style={{ height: 10, width: '80%', background: 'var(--b1)', borderRadius: 4 }} />
                </div>
              ))}
            </div>
          )}

          {!loading && !error && articles.length === 0 && (
            <div
              data-testid="blog-empty"
              className="p-10 rounded-lg text-center"
              style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
            >
              <Newspaper size={28} style={{ color: 'var(--ts)', margin: '0 auto 12px' }} />
              <p className="font-body" style={{ color: 'rgba(240,237,248,0.78)' }}>
                No articles available right now. Please check back soon.
              </p>
            </div>
          )}

          {!loading && !error && featured && (
            <ScrollReveal>
              <a
                data-testid="blog-featured"
                href={featured.link}
                target="_blank"
                rel="noreferrer noopener"
                className="group block p-6 sm:p-8 rounded-lg mb-10 transition-all"
                style={{
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(192,132,252,0.04))',
                  border: '1px solid var(--b2)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--b3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b2)'; }}
              >
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span
                    className="inline-flex items-center font-body text-xs px-2 py-1 rounded"
                    style={{ background: 'rgba(139,92,246,0.14)', color: 'var(--a-bright)' }}
                  >
                    Top Story
                  </span>
                  <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                    {featured.source}
                  </span>
                  <span className="font-body text-xs" style={{ color: 'var(--te)' }}>•</span>
                  <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                    {formatDate(featured.date)}
                  </span>
                </div>
                <h2
                  className="font-display font-extrabold tracking-[-0.02em] mb-3 transition-colors"
                  style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: 'var(--white)' }}
                >
                  {featured.title}
                </h2>
                {featured.excerpt && (
                  <p
                    className="font-body text-base max-w-3xl mb-4"
                    style={{ color: 'rgba(240,237,248,0.82)', lineHeight: '1.7' }}
                  >
                    {featured.excerpt}
                  </p>
                )}
                <div
                  className="inline-flex items-center font-body text-sm"
                  style={{ color: 'var(--a-bright)' }}
                >
                  Read at {hostnameOf(featured.link) || featured.source}
                  <ArrowUpRight size={14} className="ml-1.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </a>
            </ScrollReveal>
          )}

          {!loading && !error && rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {rest.map((a, i) => (
                <ScrollReveal key={`${a.link}-${i}`} delay={Math.min(i, 8) * 0.04}>
                  <a
                    href={a.link}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="blog-article"
                    className="group block h-full p-5 rounded-lg transition-all"
                    style={{
                      background: 'var(--l1)',
                      border: '1px solid var(--b1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--b3)';
                      e.currentTarget.style.background = 'rgba(139,92,246,0.04)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--b1)';
                      e.currentTarget.style.background = 'var(--l1)';
                    }}
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span
                        className="inline-flex items-center font-body text-xs px-2 py-0.5 rounded"
                        style={{ background: 'var(--a-10)', color: 'var(--a-bright)' }}
                      >
                        {a.source}
                      </span>
                      <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                        {formatDate(a.date)}
                      </span>
                    </div>
                    <h3
                      className="font-display text-lg font-semibold mb-2 leading-snug transition-colors"
                      style={{ color: 'var(--white)' }}
                    >
                      {a.title}
                    </h3>
                    {a.excerpt && (
                      <p
                        className="font-body text-sm mb-4"
                        style={{ color: 'rgba(240,237,248,0.72)', lineHeight: '1.65' }}
                      >
                        {a.excerpt}
                      </p>
                    )}
                    <div
                      className="inline-flex items-center font-body text-xs"
                      style={{ color: 'var(--ts)' }}
                    >
                      {hostnameOf(a.link) || a.source}
                      <ArrowUpRight size={12} className="ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </a>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 sm:py-20 relative"
        style={{ background: 'var(--bg)', boxShadow: 'inset 0 0 140px rgba(139,92,246,0.07)' }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', color: 'var(--white)' }}
            >
              Reading About It Is One Thing. <span style={{ color: 'var(--a)' }}>Being Ready</span> Is Another.
            </h2>
            <p
              className="font-body font-light text-base mb-8"
              style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.72' }}
            >
              Attacks, algorithms, and ad platforms all move faster than any blog can cover them.
              Let us help you turn headlines into a plan.
            </p>
            <a href="/pricing">
              <Button
                className="btn-shimmer font-body text-sm h-12 px-7 rounded-[3px]"
                style={{ background: 'var(--a)', color: 'var(--white)' }}
              >
                Book a Strategy Call
              </Button>
            </a>
          </ScrollReveal>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
